import { observer } from 'mobx-react';
import React from 'react';
import { makeAutoObservable } from 'mobx';
import API, {
	IDocumentSearchResult,
	IDocumentSearchResultItem,
	IEntityColumn,
	IUserConfigPackage,
} from '../../../API';
import {
	ContextualMenu,
	DetailsListLayoutMode,
	DirectionalHint,
	IColumn,
	IconButton,
	mergeStyleSets,
	SelectionMode,
	Spinner,
	SpinnerSize,
	Stack,
	StackItem,
} from '@fluentui/react';
import EntityDashboard from '../../dashboards/EntityDashboard';
import LegalEntityDocument from '../../../outlook/LegalEntityDocument';
import ScrollableDetailsList from '../ScrollableDetailsList';

export interface IDocumentSearchResultsProps {
	state: DocumentSearchResultsState;
}

const classNames = mergeStyleSets({
	tasksSummaryTableRow: {
		height: '100%',
		display: 'flex',
		alignItems: 'center',
		cursor: 'pointer'
	},
});

class DocumentSearchResults extends React.Component<
	IDocumentSearchResultsProps,
	{}
> {
	render() {
		if (this.props.state.loading) {
			return <Spinner size={SpinnerSize.large} />;
		}

		if (!this.props.state.results) {
			return <></>;
		}

		if (this.props.state.results.items.length === 0)
			return <>No matching documents.</>;

		return <>{this.getItemList()}</>;
	}

	private _getColumns(): IColumn[] {
		return this.props.state.columns.filter(c => !c.isHidden).map((column) => {
			return {
				key: column.id,
				name: column.displayName,
				fieldName: column.id,
				minWidth: column.displayWidth || 200,
				isResizable: true,
				className: '',
				onRender: (item: IDocumentSearchResultItem) => {
					if (column) {

						if (column.id === 'menu') {
							//Show an icon that looks like an ellipsis
							return <IconButton
								iconProps={{ iconName: 'More' }}
								title="Actions"
								ariaLabel="Actions"
								onClick={(ev) => {
									ev.stopPropagation();
									//need to select the item?
									this.props.state.setMenuDomTarget(ev.target as HTMLElement);
									this.props.state.setContextMenuShownForItem(item);
								}}
							/>;
						} else if (column.id === 'icon') {
							return <div style={{marginTop:4, cursor:'pointer'}} onClick={() => this.props.state.itemSelectedForPreview(item)}>{LegalEntityDocument.getIcon(item as any)}</div>;
						} else if(column.id === 'name') {
							return <div style={{cursor:'pointer'}} onClick={() => this.props.state.itemSelectedForPreview(item)}>
								<strong>{item.name}{ item.FileType ? ('.' + item.FileType) : '' }</strong>
								<Stack horizontal tokens={{ childrenGap: 30 }}>
									{ item.UIVersionStringOWSTEXT && <StackItem style={{fontSize:12}}>v{item.UIVersionStringOWSTEXT}</StackItem> }
									{ item.Size && <StackItem style={{fontSize:12}}>{this.props.state.formatFileSize(item.Size)}</StackItem> }
								</Stack>

							</div>;
						}


						var taskCustomFieldValue = (item as any)[column.id];

						if (taskCustomFieldValue !== undefined) {

							taskCustomFieldValue = EntityDashboard.displayPropertyValue(
								null,
								this.props.state.userConfig,
								{} as IEntityColumn,
								taskCustomFieldValue,
								true,
							);
						}

						if (taskCustomFieldValue !== undefined) {
							return (
								<div
									key={column.id}
									className={classNames.tasksSummaryTableRow}>
									<span>{taskCustomFieldValue}</span>
								</div>
							);
						}
					}
				},
			};
		});
	}

	private getItemList(): React.ReactNode {

		var columns = this._getColumns();

		columns.forEach((column) => {

			if (column.fieldName === 'menu' || column.fieldName === 'icon') {
				return;
			}

			column.isSorted = this.props.state.sortField === column.fieldName;
			column.isSortedDescending = !this.props.state.sortAsc;
			column.onColumnClick = (_, c) => {
				if (c.fieldName === this.props.state.sortField) {
					this.props.state.setSorting(c.fieldName, !this.props.state.sortAsc);
				} else {
					//Set the sort direction to the default (ascending) and the field to the new field
					this.props.state.setSorting(c.fieldName || '', true);
				}
			};
		});

		let ctxItems = [
							{
								key: 'preview', text: 'Preview', onClick: () => {
									this.props.state.itemSelectedForPreview(this.props.state.contextMenuShownForItem);
									this.props.state.setMenuDomTarget(undefined);
								}
							}
						];

		if(this.props.state.onAttachToEmail) {
			ctxItems.push({
								key: 'attachToEmail', text: 'Attach to Email', onClick: () => {
									this.props.state.setMenuDomTarget(undefined);
									this.props.state.itemSelectedForAttachToEmail(this.props.state.contextMenuShownForItem);
								}
							});
		}

		return (
			<>
				{this.props.state.menuDomTarget &&
					<ContextualMenu
						items={ctxItems}
						hidden={this.props.state.menuDomTarget === undefined}
						target={this.props.state.menuDomTarget}
						onItemClick={(_, item) => this.props.state.setMenuDomTarget(undefined)}
						onDismiss={() => this.props.state.setMenuDomTarget(undefined)}
						directionalHint={DirectionalHint.bottomLeftEdge}
					/>
				}
				<ScrollableDetailsList
					isHeaderVisible={true}
					layoutMode={DetailsListLayoutMode.fixedColumns}
					setKey="items"
					items={this.props.state.results?.items || []}
					columns={columns}
					selectionMode={SelectionMode.none}
					ariaLabelForGrid="Item details"
				/>
			</>
		);
	}
}

export class DocumentSearchResultsState {
	public userConfig: IUserConfigPackage;
	private searchText: string = '';

	private onItemLaunch: (item: IDocumentSearchResultItem) => void;
	public onAttachToEmail?: (item: IDocumentSearchResultItem) => void;
	onLoad?: () => void;
	includeOneDriveResults: boolean;
	contextMenuShownForItem: any;

	constructor(
		userConfig: IUserConfigPackage,
		includeOneDriveResults: boolean, //OneDrive results mean using graph, which doesn't allow returning sharepoint managed properties
		onItemLaunch: (item: IDocumentSearchResultItem) => void,
		onAttachToEmail?: (item: IDocumentSearchResultItem) => void,
		onLoad?: () => void
	) {
		makeAutoObservable(this);
		this.userConfig = userConfig;
		this.onItemLaunch = onItemLaunch;
		this.onAttachToEmail = onAttachToEmail;
		this.onLoad = onLoad;
		this.includeOneDriveResults = includeOneDriveResults;
	}

	public scopeToPath:string = '';
	public setScopeToPath(value: string) {
		this.scopeToPath = value;
	}
	
	public menuDomTarget: HTMLElement | undefined = undefined;
	public setMenuDomTarget(target: HTMLElement | undefined) {
		this.menuDomTarget = target;
	}

	public setContextMenuShownForItem(item: any) {
		this.contextMenuShownForItem = item;
	}

	public itemSelectedForPreview(item: IDocumentSearchResultItem) {
		this.onItemLaunch(item);
	}

	public itemSelectedForAttachToEmail(contextMenuShownForItem: IDocumentSearchResultItem) {
		if(this.onAttachToEmail) {
			this.onAttachToEmail(contextMenuShownForItem);
		}
	}


	public loading: boolean = false;
	public setLoading(val: boolean) {
		this.loading = val;
	}

	public sortField: string = '';
	public sortAsc: boolean = true;

	public setSorting(sortField: string, asc: boolean) {
		this.sortField = sortField;
		this.sortAsc = asc;

		this.runSearch();
	}

	public columns: IEntityColumn[] = [];
	public setColumns(columns: IEntityColumn[]) {
		this.columns = columns;
	}

	public searchScopeId: string = '';
	public setSearchScope(val: string) {
		this.searchScopeId = val || '';
		this.runSearch();
	}

	public showAllItems() {
		this.searchScopeId = '';
		this.runSearch();
	}

	public setSearchText(searchText: string) {
		this.searchText = searchText;
		this.runSearch();
	}

	public hasSearchQuery() {
		return this.searchText || this.searchScopeId;
	}

	private async runSearch(): Promise<void> {
		let searchKey = this.getSearchKey();
		this.setResults(); //Clear the results

		this.setLoading(true);

		let api = new API(await this.userConfig.getToken());
		let results = await api.documentSearch({
			searchText: this.searchText,
			sortBy: this.sortField,
			sortAsc: this.sortAsc,
			searchScopeId: this.searchScopeId,
			includeOneDriveResult: this.includeOneDriveResults,
			scopeToPath: this.scopeToPath
		});

		//If no changes have been made to the search criteria
		if (searchKey === this.getSearchKey()) {
			this.setResults(results);
			this.setLoading(false);
		}
	}

	public results?: IDocumentSearchResult;
	public setResults(results?: IDocumentSearchResult) {
		this.results = results;
		this.setColumns(results?.columns || []);

		if (this.onLoad)
			this.onLoad();
	}

	private getSearchKey() {
		return this.sortField + this.sortAsc + this.searchText + this.searchScopeId;
	}

	public formatFileSize(bytes: number): string {
		return bytes >= 1048576
			? (bytes / 1048576).toFixed(1) + ' MB'
			: (bytes / 1024).toFixed(1) + ' KB';
	}
}

export default observer(DocumentSearchResults);
